import { z } from 'zod';
import { Api } from '@automapa/svelte-automapa-template';

/**
 * Cialo zadania: JEDEN okres na wywolanie.
 *
 * BillingEngine liczy statystyki osobnym zapytaniem dla kazdej pary (miesiac, klient), wiec
 * zakladka pobiera po wejsciu wylacznie biezacy miesiac, a starsze dociaga na zadanie.
 */
export const UsageRequestSchema = z.object({
    period: z.string(),
});
export type UsageRequestType = z.infer<typeof UsageRequestSchema>;

/**
 * Jeden wiersz raportu uzycia: komplet metryk dla pary (miesiac, klient).
 *
 * Kolumny sa 1:1 z sekcja "Uzycie ogolne" raportu mailowego (../billing-app,
 * src/Etoll/EtollSummaryCommand.php) - to ten sam BillingEngine liczy jedno i drugie, wiec
 * panel i mail nie moga pokazac roznych liczb. `location_max`/`connection_max` to MAX, bo
 * backend zapisuje tam absolutne snapshoty COUNT(*); reszta to SUM, bo to przyrosty.
 *
 * Wszystkie metryki to liczby, bo backend rzutuje je ze stringow oddawanych przez PSQLConnector -
 * bez tego rzutowania `z.number()` odrzucilby cala odpowiedz.
 *
 * `expiration_date`/`is_expired` opisuja KONTO, nie miesiac: `settings` nie ma historii, wiec
 * starszy okres dostaje dzisiejszy status konta - dokladnie ten sam kompromis, co przy limitach.
 * `expiration_date === null` to konto BEZTERMINOWE (brak klucza w JSON-ie), a nie brak danych.
 *
 * Pola `*_limit` sa `nullable()`, bo `null` to BRAK limitu, a `0` to limit ZEROWY - backend
 * (Import/SettingsReadonly) rozroznia jedno od drugiego przez `array_key_exists`, wiec kontrakt
 * musi przeniesc to rozroznienie az do widoku. Limit dotyczy stanu BIEZACEGO konta
 * (`customer_config.settings_readonly` nie ma historii), a zestawiamy go z metryka miesiaca.
 * Kolejnosc pol jest 1:1 z wierszem backendu: limit stoi obok swojej metryki.
 */
export const UsageRowSchema = z.object({
    month: z.string(),
    customer_id: z.number(),
    customer_name: z.string(),
    user_email: z.string(),
    expiration_date: z.string().nullable(),
    is_expired: z.boolean(),
    location_max: z.number(),
    location_limit: z.number().nullable(),
    connection_max: z.number(),
    connection_limit: z.number().nullable(),
    transaction_count: z.number(),
    transaction_limit: z.number().nullable(),
    export_sum: z.number(),
    recalc_manual: z.number(),
    recalc_auto: z.number(),
    recalc_sum: z.number(),
    limits_enforced: z.boolean(),
});
export type UsageRowType = z.infer<typeof UsageRowSchema>;

export class UsageStatsApi {
    private static _endpoint: string = 'admin';

    public static async collectUsageData(api: Api, period: string): Promise<UsageRowType[]> {
        return api.getData<UsageRequestType, UsageRowType[]>(
            'post',
            `/${this._endpoint}/collect_usage_data`,
            { period },
            UsageRequestSchema,
            z.array(UsageRowSchema)
        );
    }
}
